import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TaskTableEmptyState from "./TaskTableEmptyState";
import noTeamsImage from "../../Images/Tasks/no-teams.svg";
import { TASK_COMPLETED_INTENSE_COLOR } from "../../utils/taskDeadlineDisplay";
import {
  TASK_STATUS_CANCELLED_COLOR,
  TASK_STATUS_IN_PROGRESS_COLOR,
  TASK_STATUS_PENDING_COLOR,
} from "../../utils/taskTemplateChipStyles";
import {
  TEAMS_OVERVIEW_DEFAULT_ROWS_PER_PAGE,
  TASK_TABLE_ROWS_PER_PAGE_OPTIONS,
} from "../../utils/taskTablePagination";
import {
  taskTableBodyScrollSx,
  taskTableBoundedPaperSx,
  taskTableFooterShrinkSx,
  taskTableNaturalPaperSx,
} from "../../utils/taskTableScrollSx";
import { taskTableOuterBorder } from "../../utils/taskTableGridSx";

const borderColor = (theme) =>
  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : theme.palette.divider;
const cellBorder = (theme) => `1px solid ${borderColor(theme)}`;

const tableSx = {
  borderCollapse: "collapse",
  tableLayout: "fixed",
  width: "100%",
  "& tbody tr:last-of-type td": {
    borderBottom: "none",
  },
};

// Vibrant person-avatar palette (shared standard across the task tables).
const PERSON_AVATAR_PALETTE = [
  "#6366f1", "#ec4899", "#f59e0b", "#10b981",
  "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6",
  "#f97316", "#06b6d4", "#84cc16", "#e11d48",
];

const personAvatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PERSON_AVATAR_PALETTE[Math.abs(hash) % PERSON_AVATAR_PALETTE.length];
};

const headerCellSx = {
  border: (theme) => cellBorder(theme),
  backgroundColor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
  backgroundImage: "none",
  color: (theme) => theme.palette.text.secondary,
  fontWeight: 700,
  letterSpacing: "0.01em",
  fontSize: { xs: "0.88rem", sm: "0.92rem", md: "0.96rem" },
  textAlign: "center",
  verticalAlign: "middle",
  py: 1.4,
  px: 0.65,
  lineHeight: 1.4,
  minHeight: 44,
};

const textBodyCellSx = {
  border: (theme) => cellBorder(theme),
  textAlign: "left",
  verticalAlign: "middle",
  py: 1.1,
  pl: 1,
  pr: 0.65,
  fontSize: { xs: "0.88rem", sm: "0.94rem", md: "0.98rem" },
  color: "text.primary",
  backgroundColor: "background.paper",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  lineHeight: 1.3,
};

const numBodyCellSx = {
  border: (theme) => cellBorder(theme),
  textAlign: "center",
  verticalAlign: "middle",
  py: 1.1,
  px: 0.5,
  fontSize: { xs: "0.88rem", sm: "0.94rem", md: "0.98rem" },
  color: "text.primary",
  backgroundColor: "background.paper",
};

const statusCountCellSx = (color) => ({
  ...numBodyCellSx,
  color,
  fontWeight: 700,
});

/** Column order: identity cols, then Pending → In Progress → Completed → Cancelled. */
const COLUMNS = [
  { key: "teamName", label: "Team Name", width: "11%" },
  { key: "leaderName", label: "Leader Name", width: "11%" },
  { key: "memberCount", label: "Team Member", width: "9%" },
  { key: "total", label: "Total Assign", width: "10%" },
  { key: "pending", label: "Pending", width: "10%", accent: TASK_STATUS_PENDING_COLOR },
  { key: "inProgress", label: "In Progress", width: "10%", accent: TASK_STATUS_IN_PROGRESS_COLOR },
  { key: "completed", label: "Completed", width: "10%", accent: TASK_COMPLETED_INTENSE_COLOR },
  { key: "cancelled", label: "Cancelled", width: "10%", accent: TASK_STATUS_CANCELLED_COLOR },
];

const COLUMN_COUNT = COLUMNS.length;

const emptyStateCellSx = {
  border: (theme) => cellBorder(theme),
  py: 3,
};

const TasksTeamsOverviewTable = ({
  rows = [],
  loading = false,
  onSelectTeam,
  usePageScroll = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TEAMS_OVERVIEW_DEFAULT_ROWS_PER_PAGE);

  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderBodyCell = (row, col) => {
    const value = row[col.key];
    const display =
      col.key === "leaderName"
        ? value || "—"
        : value ?? (col.key === "teamName" ? "—" : 0);

    let sx = { ...numBodyCellSx, width: col.width };
    if (col.key === "teamName") sx = { ...textBodyCellSx, fontWeight: 600, pl: 2.5, width: col.width };
    else if (col.key === "leaderName") sx = { ...textBodyCellSx, width: col.width };
    else if (col.accent) sx = { ...statusCountCellSx(col.accent), width: col.width };

    // Backend sends "—" as the "no leader" placeholder — treat it as empty too.
    const leaderName = value != null ? String(value).trim() : "";
    const hasLeader = leaderName.length > 0 && leaderName !== "—";
    const content =
      col.key === "leaderName" ? (
        hasLeader ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.85, minWidth: 0 }}>
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "0.6rem",
                fontWeight: 700,
                color: "#fff",
                bgcolor: personAvatarColor(leaderName),
              }}
            >
              {leaderName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              component="span"
              sx={{
                fontSize: { xs: "0.88rem", sm: "0.94rem", md: "0.98rem" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                color: "text.primary",
              }}
            >
              {leaderName}
            </Typography>
          </Box>
        ) : (
          "—"
        )
      ) : (
        display
      );

    return (
      <TableCell key={col.key} sx={sx}>
        {content}
      </TableCell>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...(usePageScroll ? taskTableNaturalPaperSx : taskTableBoundedPaperSx),
        backgroundColor: "background.paper",
        overflow: "hidden",
        boxSizing: "border-box",
        borderRadius: 2,
        border: (theme) => taskTableOuterBorder(theme),
      }}
    >
      <TableContainer sx={usePageScroll ? undefined : taskTableBodyScrollSx}>
        <Table stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    ...headerCellSx,
                    width: col.width,
                    ...(col.key === "teamName" || col.key === "leaderName"
                      ? {
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }
                      : null),
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.key} sx={{ ...(col.accent ? numBodyCellSx : textBodyCellSx), width: col.width }}>
                      {col.key === "leaderName" ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
                          <Skeleton variant="circular" width={24} height={24} />
                          <Skeleton width="55%" />
                        </Box>
                      ) : (
                        <Skeleton width={col.accent || col.key === "memberCount" || col.key === "total" ? "40%" : "60%"} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} sx={emptyStateCellSx} align="center">
                  <TaskTableEmptyState
                    image={noTeamsImage}
                    icon={GroupsOutlinedIcon}
                    color="#3b82f6"
                    title="No teams found"
                    subtitle="Teams you're part of will appear here."
                  />
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => (
                <TableRow
                  key={row.teamId}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:hover td": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(79, 195, 137, 0.10)"
                          : "rgba(14, 144, 70, 0.06)",
                    },
                  }}
                  onClick={() => onSelectTeam?.(row.teamId)}
                >
                  {COLUMNS.map((col) => renderBodyCell(row, col))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={TASK_TABLE_ROWS_PER_PAGE_OPTIONS}
        sx={{
          ...taskTableFooterShrinkSx,
          width: "100%",
          m: 0,
          borderTop: (theme) => cellBorder(theme),
          borderBottom: (theme) => cellBorder(theme),
          borderLeft: (theme) => cellBorder(theme),
          borderRight: (theme) => cellBorder(theme),
          backgroundColor: "background.paper",
          boxSizing: "border-box",
          "& .MuiTablePagination-toolbar": {
            minHeight: { xs: 48, sm: 52, md: 56 },
            px: 1.5,
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: { xs: "0.9rem", sm: "0.96rem", md: "1rem" },
          },
          "& .MuiTablePagination-select": {
            fontSize: { xs: "0.9rem", sm: "0.96rem", md: "1rem" },
          },
        }}
      />
    </Paper>
  );
};

export default TasksTeamsOverviewTable;
